"use client";

import { cn } from "@ui/lib";
import { WandSparklesIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { MODEL_LABEL_KEYS, formatDuration } from "../config";
import { type MusicShowcaseSong, musicShowcaseSongs } from "../showcase";
import type { MusicFormValues } from "../types";

/** 样片「Use these settings」：工作台监听它回填表单并滚回顶部 */
export const MUSIC_EXAMPLE_EVENT = "music-example";

export function applyMusicExample(values: MusicFormValues) {
	window.dispatchEvent(
		new CustomEvent<MusicFormValues>(MUSIC_EXAMPLE_EVENT, {
			detail: values,
		}),
	);
}

function ExampleCard({
	song,
	compact,
}: {
	song: MusicShowcaseSong;
	compact: boolean;
}) {
	const t = useTranslations("aiMusic");
	const { values } = song;
	const tags = [
		t(`workbench.modes.${values.mode}`),
		t(`workbench.model.${MODEL_LABEL_KEYS[values.model]}`),
		values.instrumental ? t("workbench.instrumental") : null,
		formatDuration(song.duration),
	].filter(Boolean);

	return (
		// min-w-0：网格项默认按内容最小宽度撑开，<audio> 和视频那一行会把 375 宽撑出横向滚动
		<article className="flex min-w-0 flex-col gap-4 rounded-20 border border-gray-4 bg-white p-4 sm:p-5">
			<div className="flex items-center gap-4">
				<img
					src={song.coverUrl}
					alt={t("examples.coverAlt", {
						title: song.title,
					})}
					width={compact ? 64 : 88}
					height={compact ? 64 : 88}
					loading="lazy"
					className={cn(
						"shrink-0 rounded-xl bg-gray-3 object-cover",
						compact ? "size-16" : "size-[88px]",
					)}
				/>
				<div className="min-w-0 flex-1">
					<h3 className="truncate font-semibold text-gray-12 text-lg">
						{song.title}
					</h3>
					<p className="mt-1 text-ink-2 text-xs">
						{tags.join(" · ")}
					</p>
				</div>
			</div>
			{/* preload="none"：页面上 8 个播放器，不点就不下载 */}
			<audio
				controls
				preload="none"
				src={song.audioUrl}
				className="h-10 w-full"
			>
				<track kind="captions" />
			</audio>
			{compact ? null : (
				<div className="flex items-start gap-4">
					<dl className="min-w-0 flex-1 space-y-3 text-sm">
						{values.mode === "simple" ? (
							<div>
								<dt className="font-medium text-gray-12">
									{t("workbench.prompt.label")}
								</dt>
								<dd className="mt-1 text-ink-2 leading-relaxed">
									{values.prompt}
								</dd>
							</div>
						) : (
							<div>
								<dt className="font-medium text-gray-12">
									{t("workbench.style.label")}
								</dt>
								<dd className="mt-1 text-ink-2 leading-relaxed">
									{values.style}
								</dd>
							</div>
						)}
						{values.lyrics ? (
							<div>
								<dt className="font-medium text-gray-12">
									{t("workbench.lyrics.label")}
								</dt>
								<dd className="mt-1 line-clamp-4 whitespace-pre-line text-ink-2 leading-relaxed">
									{values.lyrics}
								</dd>
							</div>
						) : null}
					</dl>
					{song.videoUrl ? (
						// 可视化视频是 9:16 竖屏（封面唱片 + 歌名 + 滚动歌词）；方形封面当 poster 用 cover 铺满
						<video
							controls
							preload="none"
							poster={song.coverUrl}
							src={song.videoUrl}
							className="aspect-[9/16] w-[120px] shrink-0 rounded-xl bg-black object-cover sm:w-[150px]"
						>
							<track kind="captions" />
						</video>
					) : null}
				</div>
			)}
			<button
				type="button"
				onClick={() => applyMusicExample(values)}
				className="mt-auto flex h-10 items-center justify-center gap-2 rounded-full border border-gray-5 font-medium text-gray-12 text-sm transition-colors hover:bg-gray-2"
			>
				<WandSparklesIcon className="size-4" aria-hidden />
				{t("examples.useSettings")}
			</button>
		</article>
	);
}

/** 右栏空状态（访客 / 还没出过歌）：只列封面、标题和播放器 */
export function MusicExamplesPanel() {
	const t = useTranslations("aiMusic");
	if (musicShowcaseSongs.length === 0) {
		return null;
	}
	return (
		// 只在左右分栏时放：上下堆叠（手机）时营销区的 Examples 就紧跟在下面，放了等于重复一屏
		<div className="hidden space-y-4 studio-split:block">
			<p className="text-sm font-medium text-muted-foreground">
				{t("examples.panelTitle")}
			</p>
			<div className="grid gap-4 lg:grid-cols-2">
				{musicShowcaseSongs.map((song) => (
					<ExampleCard key={song.id} song={song} compact />
				))}
			</div>
		</div>
	);
}

/** 营销区 Examples：带输入（描述 / 风格 / 歌词）和可视化视频 */
export function MusicExamplesGrid() {
	return (
		<div className="grid gap-5 md:grid-cols-2">
			{musicShowcaseSongs.map((song) => (
				<ExampleCard key={song.id} song={song} compact={false} />
			))}
		</div>
	);
}
